import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Rocket, Calendar, MapPin, Search } from 'lucide-react';
import { api, getErrorMessage } from '@/lib/api';
import { formatUtcToLocalDate } from '@/lib/dateUtils';
import toast from 'react-hot-toast';

type FilterStatus = 'all' | 'upcoming' | 'in-progress' | 'completed';
const OUTREACHES_PER_PAGE = 6;

interface CarouselImage {
  imageId: string;
  url: string;
  sequence: number;
  active: boolean;
}

interface FutureExplorersContent {
  row1Col1Html: string;
  carouselImages: CarouselImage[];
  row2Html: string;
}

interface PublishedOutreach {
  outreachId: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  location: string;
  status: string;
  imageUrl?: string;
  slug: string;
}

function getOutreachStatus(startDate: string, endDate: string): FilterStatus {
  const now = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (end < now) return 'completed';
  if (start <= now && now <= end) return 'in-progress';
  return 'upcoming';
}

export default function FutureExplorers() {
  const [content, setContent] = useState<FutureExplorersContent | null>(null);
  const [outreaches, setOutreaches] = useState<PublishedOutreach[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterStatus>('all');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);
        const [contentRes, outreachesRes] = await Promise.all([
          api.get('/public/future-explorers'),
          api.get('/public/outreaches'),
        ]);
        if (contentRes.success && contentRes.data) setContent(contentRes.data as FutureExplorersContent);
        if (outreachesRes.success && Array.isArray(outreachesRes.data)) {
          setOutreaches((outreachesRes.data as PublishedOutreach[]));
        }
      } catch (e) {
        toast.error(getErrorMessage(e));
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const filteredOutreaches = (() => {
    let list = outreaches;
    if (activeFilter !== 'all') {
      list = list.filter((o) => getOutreachStatus(o.startDate, o.endDate) === activeFilter);
    }
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      list = list.filter(
        (o) =>
          o.title.toLowerCase().includes(term) ||
          (o.description ?? '').toLowerCase().includes(term) ||
          (o.location ?? '').toLowerCase().includes(term)
      );
    }
    return [...list].sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
  })();

  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter, searchTerm]);

  const totalPages = Math.ceil(filteredOutreaches.length / OUTREACHES_PER_PAGE) || 1;
  const paginatedOutreaches = filteredOutreaches.slice(
    (currentPage - 1) * OUTREACHES_PER_PAGE,
    currentPage * OUTREACHES_PER_PAGE
  );

  const getFilterCount = (filter: FilterStatus) => {
    if (filter === 'all') return outreaches.length;
    return outreaches.filter((o) => getOutreachStatus(o.startDate, o.endDate) === filter).length;
  };

  const activeCarouselImages = (content?.carouselImages ?? [])
    .filter((img) => img.active)
    .sort((a, b) => a.sequence - b.sequence);

  const nextImage = () => {
    if (activeCarouselImages.length === 0) return;
    setCurrentImageIndex((prev) => (prev + 1) % activeCarouselImages.length);
  };

  const prevImage = () => {
    if (activeCarouselImages.length === 0) return;
    setCurrentImageIndex((prev) => (prev - 1 + activeCarouselImages.length) % activeCarouselImages.length);
  };

  const imageUrl = (url: string) => (url.startsWith('/') ? url : `/${url}`);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-16 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="container mx-auto px-4">
        {/* Header: same style as Missions page – icon + title, then subtitle */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <Rocket className="h-10 w-10 text-primary-600" />
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Future Explorers' Program</h1>
          </div>
          <p className="text-lg text-gray-600">
            Inspiring the next generation of aerospace engineers through hands-on learning and mentorship
          </p>
        </div>

        {/* Row 1: Left = About the Program + Carousel, Right = Outreach event cards (empty) */}
        <div className="max-w-7xl mx-auto mb-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Column 1: Left – About the Program on top, then image carousel (same width) */}
            <div className="lg:col-span-2 space-y-6 order-1">
              {/* About the Program – same width as carousel */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div
                  className="prose prose-lg max-w-none text-gray-700"
                  dangerouslySetInnerHTML={{
                    __html: content?.row1Col1Html ?? '<p>Content will appear here.</p>',
                  }}
                />
                <div className="mt-6 flex flex-wrap gap-3">
                  <a href="/contact" className="btn-outline">
                    Sounds like You?
                  </a>
                </div>
              </div>

              {/* Image carousel – same width as About the Program */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                {activeCarouselImages.length > 0 ? (
                  <>
                    <div className="relative h-96 bg-white">
                      <div className="absolute inset-0 bg-white" aria-hidden />
                      <div className="absolute inset-0 celebratory-stars opacity-70" aria-hidden />
                      <div className="absolute inset-0 celebratory-stars celebratory-stars-slow opacity-40" aria-hidden />
                      <img
                        src={imageUrl(activeCarouselImages[currentImageIndex].url)}
                        alt=""
                        className="relative z-10 w-full h-full object-contain"
                      />
                      {activeCarouselImages.length > 1 && (
                        <>
                          <button
                            type="button"
                            onClick={prevImage}
                            className="absolute left-2 top-1/2 z-20 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg transition-all"
                            aria-label="Previous image"
                          >
                            <ChevronLeft className="h-6 w-6 text-gray-900" />
                          </button>
                          <button
                            type="button"
                            onClick={nextImage}
                            className="absolute right-2 top-1/2 z-20 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg transition-all"
                            aria-label="Next image"
                          >
                            <ChevronRight className="h-6 w-6 text-gray-900" />
                          </button>
                        </>
                      )}
                    </div>
                    {activeCarouselImages.length > 1 && (
                      <div className="flex justify-center gap-2 p-4">
                        {activeCarouselImages.map((_, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => setCurrentImageIndex(index)}
                            className={`h-3 rounded-full transition-all ${
                              index === currentImageIndex
                                ? 'bg-primary-600 w-8'
                                : 'bg-gray-300 hover:bg-gray-400 w-3'
                            }`}
                            aria-label={`Go to image ${index + 1}`}
                          />
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="h-96 bg-white flex items-center justify-center text-gray-500">
                    No carousel images yet
                  </div>
                )}
              </div>
            </div>

            {/* Column 2: Right – Search, filters, then outreach event cards */}
            <div className="lg:col-span-3 min-h-[400px] order-2 flex flex-col">
              {/* Search + Filters (inside the outreach section) */}
              <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-4">
                <div className="relative flex-1 min-w-0">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by name, description, or location..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="input pl-10 w-full"
                  />
                </div>
                <div className="flex flex-wrap gap-2 flex-shrink-0">
                  {(['all', 'upcoming', 'in-progress', 'completed'] as const).map((filter) => (
                    <button
                      key={filter}
                      onClick={() => setActiveFilter(filter)}
                      className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 text-left ${
                        activeFilter === filter
                          ? 'bg-primary-600 text-white shadow-lg'
                          : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-primary-300 hover:bg-primary-50'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span>
                          {filter === 'all'
                            ? 'All'
                            : filter === 'upcoming'
                              ? 'Upcoming'
                              : filter === 'in-progress'
                                ? 'In Progress'
                                : 'Completed'}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            activeFilter === filter ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {getFilterCount(filter)}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {outreaches.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 flex items-center justify-center min-h-[400px] text-gray-500">
                  No upcoming outreach events at the moment.
                </div>
              ) : filteredOutreaches.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 flex flex-col items-center justify-center min-h-[300px] text-center">
                  <p className="text-gray-700 font-medium">
                    {searchTerm ? 'No outreach events match your search.' : 'No outreach events in this category.'}
                  </p>
                  <p className="text-gray-500 text-sm mt-1">
                    {searchTerm ? 'Try different keywords.' : 'Try selecting "All".'}
                  </p>
                </div>
              ) : (
                <>
                <div
                  className="grid gap-3 min-h-[400px] flex-1"
                  style={{
                    gridTemplateColumns:
                      paginatedOutreaches.length === 1
                        ? '1fr'
                        : paginatedOutreaches.length === 2
                          ? '1fr'
                          : paginatedOutreaches.length === 3
                            ? '1fr'
                            : paginatedOutreaches.length === 4
                              ? '1fr 1fr'
                              : paginatedOutreaches.length === 5
                                ? '1fr 1fr'
                                : '1fr 1fr 1fr',
                    gridTemplateRows:
                      paginatedOutreaches.length === 1
                        ? '1fr'
                        : paginatedOutreaches.length === 2
                          ? '1fr 1fr'
                          : paginatedOutreaches.length === 3
                            ? '1fr 1fr 1fr'
                            : paginatedOutreaches.length === 4
                              ? '1fr 1fr'
                              : paginatedOutreaches.length === 5
                                ? '1fr 1fr 1fr'
                                : '1fr 1fr',
                  }}
                >
                  {paginatedOutreaches.map((outreach, index) => {
                    const hasMedia = Boolean(outreach.imageUrl);
                    return (
                    <div
                      key={outreach.outreachId}
                      className="relative rounded-lg overflow-hidden flex flex-col min-h-[180px] bg-transparent"
                      style={
                        paginatedOutreaches.length === 5 && index === 4
                          ? { gridColumn: '1 / -1' }
                          : undefined
                      }
                    >
                      {/* Background: image or video */}
                      <div className="absolute inset-0 bg-white" aria-hidden />
                      <div className="absolute inset-0 celebratory-stars opacity-70" aria-hidden />
                      <div className="absolute inset-0 celebratory-stars celebratory-stars-slow opacity-40" aria-hidden />
                      {outreach.imageUrl ? (
                        <>
                          {outreach.imageUrl.match(/\.(mp4|webm|ogg)$/i) ? (
                            <video
                              src={outreach.imageUrl.startsWith('http') ? outreach.imageUrl : outreach.imageUrl}
                              className="absolute inset-0 z-[1] w-full h-full object-contain"
                              muted
                              playsInline
                              aria-hidden
                            />
                          ) : (
                            <img
                              src={outreach.imageUrl.startsWith('http') ? outreach.imageUrl : outreach.imageUrl}
                              alt=""
                              className="absolute inset-0 z-[1] w-full h-full object-contain"
                              aria-hidden
                            />
                          )}
                          <div className="absolute inset-0 z-[2] bg-gradient-to-t from-black/85 via-black/50 to-black/30" aria-hidden />
                        </>
                      ) : (
                        <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200" aria-hidden />
                      )}
                      {/* Content on top */}
                      <div className={`relative z-10 p-4 flex-1 flex flex-col min-h-0 ${hasMedia ? 'text-white' : 'text-gray-900'}`}>
                        <h3 className={`font-bold mb-1 line-clamp-2 ${hasMedia ? 'text-white drop-shadow-sm' : 'text-gray-900'}`}>{outreach.title}</h3>
                        <p className={`text-sm line-clamp-2 mb-2 ${hasMedia ? 'text-white/90 drop-shadow-sm' : 'text-gray-700'}`}>{outreach.description}</p>
                        <div className={`mt-auto space-y-1 text-xs ${hasMedia ? 'text-white/80' : 'text-gray-600'}`}>
                          <div className="flex items-center gap-1.5">
                            <Calendar className="h-3.5 w-3.5 flex-shrink-0" />
                            <span>
                              {formatUtcToLocalDate(outreach.startDate)}
                              {outreach.endDate && outreach.endDate !== outreach.startDate
                                ? ` – ${formatUtcToLocalDate(outreach.endDate)}`
                                : ''}
                            </span>
                          </div>
                          {outreach.location ? (
                            <div className="flex items-center gap-1.5">
                              <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                              <span>{outreach.location}</span>
                            </div>
                          ) : null}
                        </div>
                        <Link
                          to={`/outreach/${outreach.slug}`}
                          className={`mt-3 text-sm font-medium underline underline-offset-2 ${hasMedia ? 'text-white hover:text-white/90' : 'text-primary-700 hover:text-primary-800'}`}
                        >
                          Learn more →
                        </Link>
                      </div>
                    </div>
                  )})}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-4">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="p-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      aria-label="Previous page"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <span className="px-4 py-2 text-sm font-medium text-gray-700">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      aria-label="Next page"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </div>
                )}
                </>
              )}
            </div>
          </div>
        </div>

      </div>
      <style>{`
        .celebratory-stars {
          background-image:
            radial-gradient(circle, rgba(56, 189, 248, 0.55) 0 1px, transparent 1.6px),
            radial-gradient(circle, rgba(250, 204, 21, 0.5) 0 1.1px, transparent 1.8px),
            radial-gradient(circle, rgba(244, 114, 182, 0.45) 0 1.2px, transparent 1.9px);
          background-size: 28px 28px, 42px 42px, 56px 56px;
          background-position: 0 0, 14px 8px, 6px 18px;
          animation: twinkle-stars 2.8s ease-in-out infinite;
        }

        .celebratory-stars-slow {
          animation-duration: 4.6s;
          animation-delay: 0.8s;
        }

        @keyframes twinkle-stars {
          0%, 100% {
            opacity: 0.25;
            transform: scale(1);
          }
          50% {
            opacity: 0.95;
            transform: scale(1.02);
          }
        }
      `}</style>
    </div>
  );
}
